import React, { useState, useMemo } from 'react';
import { Field } from '@campfire/field';
import { HoverText } from '@campfire/hover-link';
import { Select } from '@campfire/select';
import { TabletButton } from '@campfire/tablet-button';
import { TimePicker } from '@campfire/time-picker';
import { RichEditorFormik } from '@campfire/campfire-rich-editor';
import {
  Box,
  Collapse,
  Grid,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@material-ui/core';
import { Form, Formik } from 'formik';
import { number as YupNumber, object as YupObject, string as YupString } from 'yup';
import { DateTime } from 'luxon';
import { Close, Edit } from '@material-ui/icons';
import { AutoLocationField } from '../../../../common/auto-location-field/AutoLocationField';
import { useCampfireQuery } from '../../../../global/network/useCampfireQuery';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';
import { useSnackbar } from '../../../../global/config/useSnackbar';
import { assertNever } from '../../../../common/functions/assert-never';
import { FormSectionV2 } from '../../../../common/FormSectionV2';
import { CreateActivityGetReportTypesV2 } from '../../activity-management/activity-form-v2/__generated__/CreateActivityGetReportTypesV2';
import { CREATE_ACTIVITY_GET_REPORT_TYPES_V2 } from '../../activity-management/activity-form-v2/activity-form-model-v2.gql';
import { FormikErrorFocus } from '../../../../common/form/FormikErrorFocus';
import {
  NO_REPORT_TYPES,
  NO_REPORT_IMPACT_TRACKED,
  NO_REPORT_NO_IMPACT,
} from '../../activity-management/activity-form-v2/activity-form-actions-v2';
import { AlertCard, getAlertCardColors } from '../../../../common/cards/alert-card/AlertCard';
import { CreateReportTypeFormV2 } from '../../activity-management/activity-form-v2/add-new-session-dialog-v2/CreateReportTypeFormV2';
import { useSaveReportTypeFetch } from '../../report-management-v2/report-types/report-type-form/report-type-form-actions';
import { ReportTypeFormValues } from '../../report-management-v2/common/report-type-form-sections/report-type-form-types';
import { transformSessionDataForAPI, transformSessionDataForEdit } from './add-one-off-session-api';

const NoReportTypeAlertCard = ({ reportTypeId }: { reportTypeId: string }) => {
  const noReportType = NO_REPORT_TYPES.find((reportType) => reportType.reportTypeId === reportTypeId);
  const title = noReportType?.name || '';
  const description = noReportType?.description || '';
  const { darkTextColor } = getAlertCardColors('info');
  return (
    <AlertCard variant='info' title={title}>
      <Typography style={{ fontSize: '14px', fontWeight: 400, color: darkTextColor }}>{description}</Typography>
    </AlertCard>
  );
};

const ValidationSchema = YupObject().shape({
  sessionName: YupString().required('Please indicate a session name'),
  sessionStartTime: YupString().nullable(),
  sessionEndTime: YupString().nullable(),
  minVolunteers: YupNumber().required('Please indicate the minimum volunteers required'),
  maxVolunteers: YupNumber()
    .nullable()
    .when('minVolunteers', (minVolunteers: number, schema: any) => {
      return minVolunteers ? schema.min(minVolunteers, 'Maximum volunteers must be at least the minimum') : schema;
    }),
  selectedReportTypeId: YupString().required('Please indicate the report type'),
  sessionLocation: YupObject().nullable(),
  sessionDescription: YupString().nullable(),
});

interface EditOneOffSessionDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (sessionData: any) => Promise<void>;
  session: any; // The session to edit
  activityDate: DateTime;
  activityId: string;
}

export const EditOneOffSessionDialog: React.FC<EditOneOffSessionDialogProps> = ({
  open,
  onClose,
  onSubmit,
  session,
  activityDate,
  activityId,
}) => {
  const { theme } = useCampfireTheme();
  const { setSnackbar } = useSnackbar();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showLocationField, setShowLocationField] = useState(Boolean(session?.activityLocation));
  const [showDescriptionField, setShowDescriptionField] = useState(Boolean(session?.description));
  const [showCreateReportType, setShowCreateReportType] = useState(false);
  const [showMaxVolunteersField, setShowMaxVolunteersField] = useState(Boolean(session?.maxVolunteers));

  // Report type saving
  const saveReportType = useSaveReportTypeFetch();

  // Fetch report types
  const { data, refetch: refetchReportTypes } = useCampfireQuery<CreateActivityGetReportTypesV2, {}>(
    CREATE_ACTIVITY_GET_REPORT_TYPES_V2
  );

  const reportTypeList = data
    ? data.vm.reportTypes
        .map((reportType) => ({
          reportTypeId: reportType.reportTypeId,
          name: reportType.name,
        }))
        .concat(NO_REPORT_TYPES)
    : NO_REPORT_TYPES;

  const initialValues = useMemo(() => {
    if (!session) {
      return {
        sessionName: '',
        sessionDescription: '',
        sessionStartTime: null,
        sessionEndTime: null,
        minVolunteers: 1,
        maxVolunteers: null,
        selectedReportTypeId: NO_REPORT_IMPACT_TRACKED,
        sessionLocation: null,
      };
    }
    return transformSessionDataForEdit(session);
  }, [session]);

  const handleSubmit = async (values: any) => {
    setIsSubmitting(true);
    try {
      const sessionData = {
        sessionId: session.sessionId,
        ...transformSessionDataForAPI({
          activityId: activityId,
          name: values.sessionName,
          description: values.sessionDescription,
          activityDate: activityDate.toISODate(),
          minVolunteers: values.minVolunteers,
          maxVolunteers: values.maxVolunteers,
          startTime: values.sessionStartTime
            ? DateTime.fromJSDate(values.sessionStartTime).toFormat('HH:mm')
            : undefined,
          endTime: values.sessionEndTime ? DateTime.fromJSDate(values.sessionEndTime).toFormat('HH:mm') : undefined,
          reportTypeId:
            values.selectedReportTypeId !== NO_REPORT_NO_IMPACT &&
            values.selectedReportTypeId !== NO_REPORT_IMPACT_TRACKED
              ? values.selectedReportTypeId
              : undefined,
          autoReport: values.selectedReportTypeId === NO_REPORT_IMPACT_TRACKED,
          activityLocation: values.sessionLocation || undefined,
        }),
      };

      await onSubmit(sessionData);
    } catch (error) {
      console.error('Failed to edit one-off session:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const validEndTimeFromStart = (values: any) => {
    return (
      !values.sessionEndTime ||
      !values.sessionStartTime ||
      DateTime.fromJSDate(values.sessionEndTime).diff(DateTime.fromJSDate(values.sessionStartTime), ['minutes'])
        .minutes >= 1
    );
  };

  const runSaveReportType = (reportTypeValues: ReportTypeFormValues) => {
    return saveReportType
      .run({
        reportTypeId: reportTypeValues.reportTypeId || undefined,
        name: reportTypeValues.reportTypeName,
        description: reportTypeValues.reportTypeDescription ?? null,
        active: reportTypeValues.active,
        items: reportTypeValues.items.map((item, order) => {
          if (item.__typename === 'VOLUNTEER_ReportTypeHeadingType') {
            return { ...item, reportTypeItemId: item.reportTypeItemId || undefined, order };
          }

          if (
            item.field.__typename === 'VOLUNTEER_BooleanFieldType' ||
            item.field.__typename === 'VOLUNTEER_CheckboxFieldType' ||
            item.field.__typename === 'VOLUNTEER_RatingFieldType' ||
            item.field.__typename === 'VOLUNTEER_TextFieldType' ||
            item.field.__typename === 'VOLUNTEER_TimeFieldType' ||
            item.field.__typename === 'VOLUNTEER_AttachmentFieldType'
          ) {
            return {
              ...item,
              reportTypeItemId: item.reportTypeItemId || undefined,
              field: { ...item.field, fieldId: item.field.fieldId || undefined, hero: item.field.isHero },
              order,
            };
          }

          if (item.field.__typename === 'VOLUNTEER_DropdownFieldType') {
            return {
              ...item,
              reportTypeItemId: item.reportTypeItemId || undefined,
              field: {
                ...item.field,
                fieldId: item.field.fieldId || undefined,
                dropdownFieldOptions: item.field.dropdownOptions.map((option, optionOrder) => {
                  return {
                    ...option,
                    order: optionOrder,
                  };
                }),
                allowMultiple: item.field.isAllowMultiple,
                hero: item.field.isHero,
              },
              order,
            };
          }

          if (item.field.__typename === 'VOLUNTEER_NumericFieldType') {
            return {
              ...item,
              reportTypeItemId: item.reportTypeItemId || undefined,
              field: { ...item.field, fieldId: item.field.fieldId || undefined, hero: item.field.isHero },
              order,
            };
          }

          return assertNever(item.field);
        }),
      })
      .then((res) => {
        if (!res.ok) {
          setSnackbar({
            open: true,
            message: 'Unable to save report type',
            variant: 'error',
          });
          return Promise.resolve();
        }

        setSnackbar({
          open: true,
          message: 'Successfully saved report type',
          variant: 'success',
        });

        return refetchReportTypes().then(() => {
          setShowCreateReportType(false);
        });
      })
      .catch(() => {
        setSnackbar({
          open: true,
          message: 'Unable to save report type',
          variant: 'error',
        });
      });
  };

  return (
    <Dialog open={open} maxWidth='md' fullWidth>
      <DialogTitle>
        <Grid container alignItems='center' justify='space-between'>
          <Grid item>
            <Box display='flex' alignItems='center'>
              <Edit style={{ marginRight: 8, color: theme.palette.primary.main }} />
              <Box>
                <Typography variant='h6' style={{ fontWeight: 600 }}>
                  Edit One-Off Session
                </Typography>
                <Typography variant='body2' color='textSecondary'>
                  {session?.name} - {activityDate.toFormat('EEEE, MMMM d, yyyy')}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item>
            <IconButton onClick={onClose} size='small'>
              <Close />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>

      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={ValidationSchema}
        onSubmit={handleSubmit}
      >
        {({ values }) => (
          <Form>
            <FormikErrorFocus />

            <DialogContent>
              <Typography variant='body2' color='textSecondary' style={{ marginBottom: 24 }}>
                Update the details for this one-off session that only appears on this specific date.
              </Typography>

              {/* Session Basics */}
              <FormSectionV2 title='Session Details'>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Field
                      name='sessionName'
                      label='Session Name'
                      fullWidth
                      variant='outlined'
                      helper
                      placeholder='e.g., Special Holiday Session'
                    />
                  </Grid>

                  {!showDescriptionField ? (
                    <Grid item xs={12}>
                      <HoverText variant='body2' color='primary' onClick={() => setShowDescriptionField(true)}>
                        {session?.description ? 'Edit description' : 'Add a description'}
                      </HoverText>
                    </Grid>
                  ) : (
                    <Grid item xs={12}>
                      <Collapse in={showDescriptionField}>
                        <RichEditorFormik valueName='sessionDescription' placeholder='Description (Optional)' />
                      </Collapse>
                    </Grid>
                  )}

                  {!showLocationField ? (
                    <Grid item xs={12}>
                      <HoverText variant='body2' color='primary' onClick={() => setShowLocationField(true)}>
                        {session?.activityLocation ? 'Edit location' : 'Add a different location'}
                      </HoverText>
                    </Grid>
                  ) : (
                    <Grid item xs={12}>
                      <Collapse in={showLocationField}>
                        <AutoLocationField
                          name='sessionLocation'
                          label='Location'
                          helper
                          fullWidth
                          variant='outlined'
                        />
                      </Collapse>
                    </Grid>
                  )}
                </Grid>
              </FormSectionV2>

              {/* Time Settings */}
              <FormSectionV2 title='Time'>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TimePicker name='sessionStartTime' label='Start time' inputVariant='outlined' />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TimePicker name='sessionEndTime' label='End time' inputVariant='outlined' />
                    {!validEndTimeFromStart(values) && (
                      <Typography display='block' variant='caption' color='error'>
                        End time must be later than start time
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </FormSectionV2>

              {/* Volunteers */}
              <FormSectionV2 title='Volunteers'>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Field
                      name='minVolunteers'
                      label='Minimum volunteers'
                      type='number'
                      fullWidth
                      variant='outlined'
                      helper
                      inputProps={{ min: 0 }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    {!showMaxVolunteersField && !values.maxVolunteers ? (
                      <Box pt={2}>
                        <HoverText color='primary' variant='body2' onClick={() => setShowMaxVolunteersField(true)}>
                          Add maximum number of volunteers
                        </HoverText>
                      </Box>
                    ) : (
                      <Box>
                        <Field
                          name='maxVolunteers'
                          label='Maximum volunteers (optional)'
                          type='number'
                          fullWidth
                          variant='outlined'
                          helper
                          inputProps={{ min: 0 }}
                        />
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </FormSectionV2>

              {/* Report Type */}
              <FormSectionV2 title='Report Type'>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Select
                      key={`select-${reportTypeList.length}-${data ? 'loaded' : 'loading'}`}
                      fullWidth
                      variant='outlined'
                      name='selectedReportTypeId'
                      label='Report type'
                      helper
                      items={reportTypeList}
                      renderFunction={(item: { name: string; reportTypeId: string }) => item.name}
                      valueFunction={(item: { name: string; reportTypeId: string }) => item.reportTypeId}
                    />
                  </Grid>

                  {(values.selectedReportTypeId === NO_REPORT_NO_IMPACT ||
                    values.selectedReportTypeId === NO_REPORT_IMPACT_TRACKED) && (
                    <Grid item xs={12}>
                      <NoReportTypeAlertCard reportTypeId={values.selectedReportTypeId} />
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <HoverText variant='body2' color='primary' onClick={() => setShowCreateReportType(true)}>
                      Create new report type
                    </HoverText>
                  </Grid>

                  {showCreateReportType && (
                    <Grid item xs={12}>
                      <Collapse in={showCreateReportType}>
                        <Box bgcolor='aliceblue' border={1} borderColor='primary.main' borderRadius={1} p={2}>
                          <CreateReportTypeFormV2
                            onSubmit={runSaveReportType}
                            isLoading={saveReportType.isLoading}
                            cancel={() => setShowCreateReportType(false)}
                          />
                        </Box>
                      </Collapse>
                    </Grid>
                  )}
                </Grid>
              </FormSectionV2>
            </DialogContent>

            <DialogActions>
              <TabletButton onClick={onClose} disabled={isSubmitting}>
                Cancel
              </TabletButton>
              <TabletButton type='submit' variant='contained' color='primary' disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </TabletButton>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};
